import React, { useContext, useState } from 'react'
import { View, StyleSheet, Platform, ScrollView, SafeAreaView, TouchableOpacity, Image } from 'react-native'
import { Icon, Text, Button, Modal, Card, Input, Spinner, Toggle, Datepicker, OverflowMenu, MenuItem, useTheme } from '@ui-kitten/components'
import { format } from 'date-fns'

import * as Clipboard from 'expo-clipboard'
import * as SMS from 'expo-sms'
import { UserContext } from '../../global/UserProvider'
import { userLocaleAtom } from '../../global/AtomsProvider'
import base64 from '../../utils/base64'
import { CartView } from '../../components/CartView'
import { deleteSaleReturn, markSalePaid, mirrorSaleFiscalInfo, updateSale, updateStoreWiponCredentials } from '../../global/Firebase/firebase'
import { saveFiscalOperation, setNewLKTokens } from '../../global/lightkassa'
import { FontAwesome5 } from '@expo/vector-icons'
import { PaymentMethodsSelect } from '../../components/PaymentMethodsSelect'
import { safePrintAsync } from '../../utils/printUtils'
import * as Sentry from "@sentry/react-native"
import { useToast } from "react-native-toast-notifications"
import { fiscalizeSale } from '../../global/wipon'
import { useColors } from '../../utils/useColors'
import { useTranslation } from 'react-i18next'
import { constantsAtom, organizationAtom, storesDictAtom, userRoleAtom, usersDictAtom, productUnitsDictAtom, currencyFormatterAtom, newSaleInterfaceSettingsAtom, localeDateServiceAtom, currencyAtom, salePrintTemplatesAtom, productsDictAtom, priceTypesAtom } from '../../global/AtomsProvider'
import { getLocalStoreDate, getDailyStatID } from '../../utils/timezoneFunctions'
import { getDefaultStore, useAtomValue } from 'jotai'
import i18n from 'i18next'
import { getPrintTemplateSettings } from '../../utils/printTemplateUtils'
import { generateInvoiceNumber } from '../../utils/invoiceUtils'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { app } from '../../global/Firebase/firebaseFirestore'
import { getFiscalCredentials, getFiscalOperator, isFiscalizationEnabled } from '../../utils/fiscalization'
import { fiscalFailureDetail, forgetResolvedSales, unfiscalizedSaleKey } from '../../utils/unfiscalizedSales'
import { WhatsAppButton } from '../../components/WhatsAppButton'
import { fiscalReceiptPrintBody, fiscalReceiptShareText } from '../../utils/fiscalReceiptContent'
import { readMasterFiscalState } from '../../utils/fiscalRetryGuard'

/**
 * Public download URL for a product image in the CONFIGURED Firebase project's
 * Storage bucket.
 *
 * The bucket used to be the production project's id hardcoded inline, which meant a
 * staging build read product images out of the PRODUCTION bucket regardless of which
 * project everything else pointed at — the one isolation hole the staging environment's
 * config-level guards could not see, because it is baked into the bundle rather than
 * read from config.
 *
 * `app.options.storageBucket` is correct on both platforms: on web it comes from
 * EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET via firebaseConfig, and on native from the
 * GoogleService file the build variant selects. Reading it here rather than importing
 * firebaseConfig directly matters — firebaseConfig throws on missing env vars and is
 * web-only, so importing it into a shared screen would break native builds.
 */
const productImageUrl = (fileName) =>
  `https://firebasestorage.googleapis.com/v0/b/${app.options.storageBucket}/o/productImages%2F${encodeURIComponent(fileName)}?alt=media`

// Preload product images to ensure they're cached before printing
const preloadProductImages = async (sale, productsDict) => {
  const imagePromises = []
  const cartItems = sale.cartItems || []
  
  for (const item of cartItems) {
    if (!item) continue
    const itemId = item.id || item.productId
    if (itemId && productsDict && productsDict[itemId]) {
      const product = productsDict[itemId]
      if (product && product.productImages && product.productImages.length > 0) {
        const firstImage = product.productImages[0]
        if (firstImage && firstImage.fileName) {
          const imageUrl = productImageUrl(firstImage.fileName)

          // Create a promise that preloads the image using React Native Image.prefetch
          const imagePromise = Image.prefetch(imageUrl).then(() => {
            // Image prefetched successfully
          }).catch(() => {
            // Image failed to prefetch, but we don't want to block the process
          })
          
          imagePromises.push(imagePromise)
        }
      }
    }
  }
  
  if (imagePromises.length > 0) {
    await Promise.all(imagePromises)
  }
}

// Preload extra image to ensure it's cached before printing
const preloadExtraImage = async (templateSettings) => {
  if (!templateSettings?.images?.extra?.enabled || !templateSettings?.images?.extra?.url) {
    return
  }
  
  try {
    await Image.prefetch(templateSettings.images.extra.url)
  } catch (error) {
    console.warn('Failed to preload extra image:', error)
  }
}

// Preload logo image to ensure it's cached before printing
const preloadLogoImage = async (templateSettings) => {
  if (!templateSettings?.images?.logo?.enabled || !templateSettings?.images?.logo?.url) {
    return
  }
  
  try {
    await Image.prefetch(templateSettings.images.logo.url)
  } catch (error) {
    console.warn('Failed to preload logo image:', error)
  }
}

// Print using a hidden iframe: write HTML, wait for resources, print, cleanup
const printInIframe = async (title, bodyInnerHtml) => {
  try {
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument || iframe.contentWindow.document
    doc.open()
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>div{white-space:pre-wrap;margin:10px 0}@media print{@page{margin:0}}</style></head><body>${bodyInnerHtml}</body></html>`)
    doc.close()

    // Wait for images with longer timeout and better error handling
    const images = Array.from(doc.images || [])
    if (images.length > 0) {
      await Promise.all(images.map((img, index) => {
        if (img.complete && img.naturalWidth !== 0) {
          return Promise.resolve()
        }
        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            resolve()
          }, 10000) // 10 second timeout per image
          
          img.addEventListener('load', () => {
            clearTimeout(timeout)
            resolve()
          }, { once: true })
          
          img.addEventListener('error', (e) => {
            clearTimeout(timeout)
            resolve()
          }, { once: true })
        })
      }))
    }
    
    // Wait for fonts if available
    try { 
      if (doc.fonts && doc.fonts.ready) { 
        await doc.fonts.ready 
      } 
    } catch(e) {
      // Intentionally ignore font loading errors
    }

    // Additional delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 500))

    setTimeout(() => {
      try { 
        iframe.contentWindow.focus(); 
        iframe.contentWindow.print() 
      } catch(e) {
        // Intentionally ignore print errors
      }
      setTimeout(() => { 
        try { 
          document.body.removeChild(iframe) 
        } catch(e) {
          // Intentionally ignore cleanup errors
        }
      }, 1000) // Increased cleanup delay
    }, 100) // Increased initial delay
  } catch(e) {
    // Intentionally ignore print iframe errors
  }
}

export const generateInternalReceipt = (sale, orgName, storeName, cashierName, localStoreDate, currencyFormatter, productUnitsDict, defaultUnit, address=null) => {
  let width = 58
  let fontSize = 20
  let jotaiStore =  getDefaultStore()
  let newSaleInterfaceSettings = jotaiStore.get(newSaleInterfaceSettingsAtom)
  if (newSaleInterfaceSettings.internalReceiptFontSize) fontSize = newSaleInterfaceSettings.internalReceiptFontSize
  if (newSaleInterfaceSettings.internalReceiptWidth) width = newSaleInterfaceSettings.internalReceiptWidth
  let text = `<div style="width: ${width}mm; font-size: ${fontSize}; font-weight: 500; color: black;">`
  text += `<p style="text-align: center">${orgName}<br>` 
  text += `<span style="text-align: center">${i18n.t('salesHistory.cashbox')} ${storeName}</span><br>`
  if (address != null && address !== '') {
    text += `<span style="text-align: center">${address}</span><br>`
  }
  text += `<span style="text-align: center">${i18n.t('salesHistory.cashier')}: ${cashierName}</span><br>`
  if (sale.client) {
    text += `<span style="text-align: center">${i18n.t('salesHistory.client')}: ${sale.client.displayName}, ${sale.client.phoneNumber}</span><br>`
  }
  // Show delivery address if available, otherwise show client address
  const addressToShow = sale.deliveryAddress || (sale.client && sale.client.address)
  if (addressToShow) {
    const addressLabel = sale.deliveryAddress ? i18n.t('salesHistory.deliveryAddress') : i18n.t('salesHistory.clientAddress')
    text += `<span style="text-align: center">${addressLabel}: ${addressToShow}</span><br>`
  }
  text += `<span style="text-align: center">${format(localStoreDate, 'd MMMM y HH:mm')}</span></p>`
  text += '<div style="margin: 10px 0;"></div>'
  const cartItems = sale.cartItems || []
  const saleStore = sale.store || sale.storeId
  for (let item of cartItems) {
    if (!item) continue
    text += '<p>'
    const itemName = item.name || item.product?.name || 'Unknown'
    const itemQuantity = item.quantity || 0
    const itemPrice = item.salePrices?.[saleStore] || item.salePrice || item.price || 0
    const itemUnit = item.productUnit || item.unit || 'pcs'
    text += `<div style="display: flex; justify-content: space-between"><span>${itemName}</span></div>`
    text += `<div style="display: flex; justify-content: space-between"><span>${itemQuantity} ${productUnitsDict?.[itemUnit]?.shortName || defaultUnit} x ${itemPrice}</span><span>${currencyFormatter.format(itemQuantity * itemPrice)}</span></div>`
    if (item.itemDiscount?.percentage > 0) {
      text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.discount')}: ${item.itemDiscount?.percentage}%</span><span>${currencyFormatter.format(item.finalItemPrice || itemQuantity * itemPrice)}</span></div>`
    }
    text += '</p>'
  }
  text += '<div style="margin: 10px 0;"></div>'
  text += '<p>'
  const salePriceModificators = sale.priceModificators || sale.purchaseMethod?.priceModificators
  if (salePriceModificators?.length > 0) {
    for (let priceModificator of salePriceModificators) {
      const totalCartPrice = sale.totalCartPrice || sale.totalPrice || 0
      text += `<div style="display: flex; justify-content: space-between"><span>${priceModificator.name}</span><span>${currencyFormatter.format(totalCartPrice*priceModificator.value/100)}</span></div>`
    }
  }
  if (sale.discount || (sale.discountPercentage != null && sale.discountPercentage != 0)) {
    text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.totalWithoutDiscount')}: </span><span style="text-align: end; float: right;">${currencyFormatter.format(sale.totalCartPriceWithPriceModificators)}</span></div>`
    text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.totalDiscount')}: </span><span style="text-align: end; float: right;">${sale.discountPercentage ?? sale.discount}%</span></div>`
  } 
  if (sale.discountAmount != null && sale.discountAmount != 0 ) {
    text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.totalWithoutDiscount')}: </span><span style="text-align: end; float: right;">${currencyFormatter.format(sale.totalCartPriceWithPriceModificators)}</span></div>`
    text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.totalDiscount')}: </span><span style="text-align: end; float: right;">${currencyFormatter.format(sale.discountAmount)}</span></div>`
  } 
  if (sale.bonusReduction) {
    text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.bonusReduction')}: </span><span>${currencyFormatter.format(sale.bonusReduction)}</span></div>`
  }
  if (sale.punchCardReduction) {
    text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.bonusReduction')}: </span><span>${currencyFormatter.format(sale.punchCardReduction)}</span></div>`
  }
  text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.total')}: </span><span>${currencyFormatter.format(sale.finalTotalCartPrice || sale.totalCartPrice || sale.totalPrice || 0)}</span></div>`
  if (sale.paymentMethod?.name) {
    text += `<div style="display: flex; justify-content: space-between"><span>${i18n.t('salesHistory.payment')}: </span><span style="text-align: end; float: right;">${sale.paymentMethod?.name}</span></div>`
  }
  text += '</p>'
  text += '<div style="margin: 10px 0;"></div>'
  text += `<p style="text-align: center">${i18n.t('salesHistory.thankYouForYourPurchase')}</p>` 
  text += '</div>'
  return text
}

export const generateExpenseInvoice = (sale, orgName, storeName, cashierName, localStoreDate, currencyFormatter, currency, productUnitsDict, defaultUnit, address=null, invoiceNumber=null, qrCodeImage=null, userLocale='ru', invoiceSettings=null, t=null, productsDict=null) => {
  
  // Default settings
  const defaultSettings = {
    title: t ? t('invoice.sale') : 'Продажа',
    subtitle: '',
    includeFields: {
      invoiceNumber: true,
      date: true,
      supplier: true,
      buyer: true,
      clientPhone: false,
      clientAddress: false,
      itemsTable: true,
      productImages: false,
      totalAmount: true,
      amountInWords: true,
      signatures: true
    },
    includeVAT: false,
    vatRate: 12,
    additionalText: '',
    images: {
      logo: { enabled: false, url: null },
      extra: { enabled: false, url: null, widthPx: 80 }
    },
    signatureFields: {
      released: { enabled: true, label: t ? t('invoice.released') : 'отпустил' },
      received: { enabled: true, label: t ? t('invoice.received') : 'получил' }
    },
    pageSettings: {
      width: 'A4',
      customWidth: 210,
      padding: 10
    }
  }
  
  // Merge with provided settings
  const settings = invoiceSettings ? {
    ...defaultSettings,
    ...invoiceSettings,
    includeFields: {
      ...defaultSettings.includeFields,
      ...(invoiceSettings.includeFields || {})
    },
    images: {
      ...defaultSettings.images,
      ...(invoiceSettings.images || {})
    },
    signatureFields: {
      ...defaultSettings.signatureFields,
      ...(invoiceSettings.signatureFields || {})
    },
    pageSettings: {
      ...defaultSettings.pageSettings,
      ...(invoiceSettings.pageSettings || {})
    }
  } : defaultSettings
  
  // Generate invoice number if not provided
  const invoiceNum = invoiceNumber || generateInvoiceNumber(localStoreDate)
  
  // Format date for the invoice
  const invoiceDate = format(localStoreDate, 'd MMMM y')
  
  // Calculate total amount
  const totalAmount = sale.finalTotalCartPrice || sale.totalCartPrice || sale.totalPrice || 0
  
  // Calculate VAT if enabled
  const vatAmount = settings.includeVAT ? (totalAmount * settings.vatRate / 100) : 0
  const totalWithVAT = settings.includeVAT ? totalAmount + vatAmount : totalAmount
  
  // Convert number to words with proper currency spelling
  const { currencyToWordsAuto } = require('../../utils/currencyToWords')
  const amountInWords = currencyToWordsAuto(totalWithVAT, currency, userLocale)
  
  const renderLogo = () => {
    if (!settings.images?.logo?.enabled || !settings.images?.logo?.url) return ''
    return `<div style="text-align: left;"><img src="${settings.images.logo.url}" style="width: 40px; height: 40px; object-fit: contain;" /></div>`
  }
  
  const renderExtraImage = () => {
    const extraEnabled = settings.images?.extra?.enabled
    const extraUrl = settings.images?.extra?.url
    const extraWidth = settings.images?.extra?.widthPx || 80
    if (!extraEnabled || !extraUrl) return ''
    return `<div style="margin-top: 8px; text-align: left;"><img src="${extraUrl}" style="width: ${parseInt(extraWidth, 10)}px; height: ${parseInt(extraWidth, 10)}px; object-fit: contain;" /></div>`
  }
  
  // Calculate page width based on settings
  let pageWidth = 210 // Default A4 width
  if (settings.pageSettings?.width === 'receipt') {
    pageWidth = 58
  } else if (settings.pageSettings?.width === 'custom') {
    pageWidth = settings.pageSettings?.customWidth || 210
  } else if (settings.pageSettings?.width === 'A4') {
    pageWidth = 210
  }
  
  const padding = settings.pageSettings?.padding || 10
  
  // Build the HTML content
  let html = `<div style="width: ${pageWidth}mm; margin: 0; padding: ${padding}px; font-family: Arial, sans-serif; font-size: 10px; line-height: 1; color: black; background: white; position: relative;">
  <style>
    @media print {
      table { table-layout: fixed !important; }
      td, th { word-wrap: break-word; overflow-wrap: break-word; }
    }
  </style>`
  
  // Add logo if enabled
  html += renderLogo()
  
  // Add header
  html += '<div style="text-align: center;">'
  html += `<div style="font-size: 12px; font-weight: bold;">${settings.title}`
  if (settings.includeFields.invoiceNumber) {
    html += ` №${invoiceNum}`
  }
  if (settings.includeFields.date) {
    const fromText = t ? t('invoice.from') : 'от'
    const yearText = t ? t('invoice.year') : 'г.'
    html += ` ${fromText} ${invoiceDate}${yearText}`
  }
  html += '</div>'
  html += `<div style="font-size: 10px;">${settings.subtitle}</div>`
  html += '</div>'
  html += '<div style="margin: 15px 0;"></div>'
  
  // Add supplier/buyer info
  if (settings.includeFields.supplier || settings.includeFields.buyer) {
    html += '<div>'
    if (settings.includeFields.supplier) {
      const supplierText = t ? t('invoice.supplier') : 'Поставщик'
      html += `<div><strong>${supplierText}:</strong> ${orgName}</div>`
    }
    // Display buyer name and phone on the same line, address separately
    if (settings.includeFields.buyer && sale.client?.displayName) {
      const buyerText = t ? t('invoice.buyer') : 'Покупатель'
      let buyerLine = `<strong>${buyerText}:</strong> ${sale.client.displayName}`
      
      // Add phone to the same line without label
      if (settings.includeFields.clientPhone && sale.client?.phoneNumber) {
        buyerLine += `, ${sale.client.phoneNumber}`
      }
      
      html += `<div>${buyerLine}</div>`
    }
    
    // Display address on separate line with label - check delivery address first, then client address
    if (settings.includeFields.clientAddress) {
      const addressToShow = sale.deliveryAddress || (sale.client && sale.client.address)
      if (addressToShow) {
        const addressLabel = sale.deliveryAddress ? 
          (t ? t('invoice.deliveryAddress') : 'Адрес доставки') : 
          (t ? t('invoice.clientAddress') : 'Адрес клиента')
        html += `<div><strong>${addressLabel}:</strong> ${addressToShow}</div>`
      }
    }
    html += '</div>'
  }
  html += '<div style="margin: 15px 0;"></div>'
  
  // Add items table
  if (settings.includeFields.itemsTable) {
    html += '<table style="width: 100%; border-collapse: collapse; font-size: 8px; table-layout: fixed;"><thead>'
    const itemNumberText = t ? t('invoice.itemNumber') : '№'
    const itemNameText = t ? t('invoice.itemName') : 'Наименование'
    const unitText = t ? t('invoice.unit') : 'Ед.'
    const quantityText = t ? t('invoice.quantity') : 'Кол-во'
    const priceText = t ? t('invoice.price') : 'Цена'
    const amountText = t ? t('invoice.amount') : 'Сумма'
    const vatText = t ? t('invoice.vat') : 'НДС'
    const imageText = t ? t('invoice.productImage') : 'Изображение'
    
    // Calculate column widths based on whether product images are included
    const includeImages = settings.includeFields.productImages
    const baseColumns = includeImages ? 7 : 6 // +1 for images column
    const vatColumns = settings.includeVAT ? 1 : 0
    const totalColumns = baseColumns + vatColumns
    
    // Define custom column widths: item number smaller, item name larger
    const itemNumberWidth = 8 // Smaller width for item number (just numbers)
    const imageWidth = includeImages ? 12 : 0 // Fixed width for images
    const itemNameWidth = 35 // Larger width for item name
    
    // Calculate remaining width and distribute among other columns
    const fixedWidths = itemNumberWidth + imageWidth + itemNameWidth
    const remainingWidth = 100 - fixedWidths
    const otherColumnsCount = totalColumns - (includeImages ? 3 : 2) // 3 if images included, 2 if not
    const otherColumnsWidth = Math.floor(remainingWidth / otherColumnsCount)
    
    html += '<tr style="background-color: #f5f5f5;">'
    html += `<th style="border: 1px solid #000; padding: 1px; text-align: center; width: ${itemNumberWidth}%; min-width: 20px;">${itemNumberText}</th>`
    if (includeImages) {
      html += `<th style="border: 1px solid #000; padding: 1px; text-align: center; width: ${imageWidth}%; min-width: 30px;">${imageText}</th>`
    }
    html += `<th style="border: 1px solid #000; padding: 1px; text-align: left; width: ${itemNameWidth}%; min-width: 80px;">${itemNameText}</th>`
    html += `<th style="border: 1px solid #000; padding: 1px; text-align: center; width: ${otherColumnsWidth}%; min-width: 30px;">${unitText}</th>`
    html += `<th style="border: 1px solid #000; padding: 1px; text-align: center; width: ${otherColumnsWidth}%; min-width: 30px;">${quantityText}</th>`
    html += `<th style="border: 1px solid #000; padding: 1px; text-align: center; width: ${otherColumnsWidth}%; min-width: 40px;">${priceText}</th>`
    html += `<th style="border: 1px solid #000; padding: 1px; text-align: center; width: ${otherColumnsWidth}%; min-width: 40px;">${amountText}</th>`
    if (settings.includeVAT) {
      html += `<th style="border: 1px solid #000; padding: 1px; text-align: center; width: ${otherColumnsWidth}%; min-width: 30px;">${vatText}</th>`
    }
    html += '</tr></thead><tbody>'
    
    // Add items to table
    const cartItems = sale.cartItems || []
    const saleStore = sale.store || sale.storeId
    cartItems.forEach((item, index) => {
      if (!item) return
      const unitName = productUnitsDict?.[item.productUnit || item.unit]?.shortName || defaultUnit || 'ШТ'
      const price = item.salePrices?.[saleStore] || item.salePrice || item.price || 0
      const quantity = item.quantity || 0
      const amount = price * quantity
      
      // Get product image if available
      let productImageHtml = ''
      const itemId = item.id || item.productId
      if (includeImages && productsDict && itemId) {
        const product = productsDict[itemId]
        if (product && product.productImages && product.productImages.length > 0) {
          const firstImage = product.productImages[0]
          if (firstImage && firstImage.fileName) {
            // Construct the image URL for Firebase Storage
            const imageUrl = productImageUrl(firstImage.fileName)
            productImageHtml = `<td style="border: 1px solid #000; padding: 1px; text-align: center; width: ${imageWidth}%; height: 30px; min-width: 30px;"><img src="${imageUrl}" style="width: 25px; height: 25px; object-fit: cover; border-radius: 2px;" onerror="this.style.display='none'; this.parentNode.innerHTML='<div style=&quot;width: 25px; height: 25px; background-color: #f0f0f0; border: 1px solid #ccc; display: flex; align-items: center; justify-content: center; font-size: 6px;&quot;>IMG</div>';" /></td>`
          }
        }
      }
      
      html += '<tr>'
      html += `<td style="border: 1px solid #000; padding: 1px; text-align: center; width: ${itemNumberWidth}%; min-width: 20px;">${index + 1}</td>`
      if (includeImages) {
        html += productImageHtml || `<td style="border: 1px solid #000; padding: 1px; text-align: center; width: ${imageWidth}%; height: 30px; min-width: 30px;"></td>`
      }
      const itemName = item.name || item.product?.name || 'Unknown'
      html += `<td style="border: 1px solid #000; padding: 1px; width: ${itemNameWidth}%; min-width: 80px;">${itemName}</td>`
      html += `<td style="border: 1px solid #000; padding: 1px; text-align: center; width: ${otherColumnsWidth}%; min-width: 30px;">${unitName}</td>`
      html += `<td style="border: 1px solid #000; padding: 1px; text-align: center; width: ${otherColumnsWidth}%; min-width: 30px;">${quantity}</td>`
      html += `<td style="border: 1px solid #000; padding: 1px; text-align: right; width: ${otherColumnsWidth}%; min-width: 40px;">${currencyFormatter.format(price)}</td>`
      html += `<td style="border: 1px solid #000; padding: 1px; text-align: right; width: ${otherColumnsWidth}%; min-width: 40px;">${currencyFormatter.format(amount)}</td>`
      if (settings.includeVAT) {
        html += `<td style="border: 1px solid #000; padding: 1px; text-align: right; width: ${otherColumnsWidth}%; min-width: 30px;">${currencyFormatter.format(amount * settings.vatRate / 100)}</td>`
      }
      html += '</tr>'
    })
    
    html += '</tbody></table>'
  }
  html += '<div style="margin: 15px 0;"></div>'
  
  // Add totals
  if (settings.includeFields.totalAmount) {
    const totalText = t ? t('invoice.total') : 'Итого'
    const vatText = t ? t('invoice.vat') : 'НДС'
    const totalWithVatText = t ? t('invoice.totalWithVat') : 'Итого с НДС'
    const withoutTaxText = t ? t('invoice.withoutTax') : 'Без налога (НДС)'
    
    html += '<div>'
    html += '<div style="display: flex; justify-content: space-between;">'
    html += `<span><strong>${totalText}:</strong></span>`
    html += `<span><strong>${currencyFormatter.format(totalAmount)}</strong></span>`
    html += '</div>'
    
    if (settings.includeVAT) {
      html += '<div style="display: flex; justify-content: space-between;">'
      html += `<span>${vatText} (${settings.vatRate}%):</span>`
      html += `<span>${currencyFormatter.format(vatAmount)}</span>`
      html += '</div>'
      html += '<div style="display: flex; justify-content: space-between;">'
      html += `<span><strong>${totalWithVatText}:</strong></span>`
      html += `<span><strong>${currencyFormatter.format(totalWithVAT)}</strong></span>`
      html += '</div>'
    } else {
      html += '<div style="display: flex; justify-content: space-between;">'
      html += `<span>${withoutTaxText}:</span>`
      html += '<span>-</span>'
      html += '</div>'
    }
    html += '</div>'
  }
  html += '<div style="margin: 15px 0;"></div>'
  
  // Add amount in words
  if (settings.includeFields.amountInWords) {
    const totalItemsText = t ? t('invoice.totalItems') : 'Всего наименований'
    const onAmountText = t ? t('invoice.onAmount') : 'на сумму'
    
    html += '<div>'
    const cartItems = sale.cartItems || []
    html += `<div>${totalItemsText} ${cartItems.length}, ${onAmountText} ${currencyFormatter.format(totalWithVAT)} (${amountInWords})</div>`
    html += '</div>'
  }
  html += '<div style="margin: 15px 0;"></div>'
  
  // Add signatures (render if not explicitly disabled, or if any individual signature is enabled)
  if (settings.includeFields.signatures !== false) {
    const releasedEnabled = settings.signatureFields?.released?.enabled !== false
    const receivedEnabled = settings.signatureFields?.received?.enabled !== false
    const releasedText = settings.signatureFields?.released?.label || (t ? t('invoice.released') : 'отпустил')
    const receivedText = settings.signatureFields?.received?.label || (t ? t('invoice.received') : 'получил')

    if (releasedEnabled || receivedEnabled) {
      html += '<div>'
      html += '<div style="display: flex; justify-content: space-between;">'
      if (releasedEnabled) {
        html += `<div style="width: 45%; border-bottom: 1px solid #000; min-height: 10px;">${releasedText}:</div>`
      } else {
        html += '<div style="width: 45%"></div>'
      }
      if (receivedEnabled) {
        html += `<div style="width: 45%; border-bottom: 1px solid #000; min-height: 10px;">${receivedText}:</div>`
      } else {
        html += '<div style="width: 45%"></div>'
      }
      html += '</div>'
      html += '</div>'
    }
  }
  html += '<div style="margin: 15px 0;"></div>'
  
  // Add additional text
  if (settings.additionalText) {
    html += '<div style="margin-top: 10px;">'
    html += `<div>${settings.additionalText}</div>`
    html += '</div>'
  }
  
  // Add extra image at the bottom if enabled
  html += renderExtraImage()
  
  html += '</div>'
  
  return html
}

/**
 * Find a user by any ID format (Supabase UUID, Firestore ID, or camelCase field)
 * This handles the case where usersDict might be keyed by Firestore ID but
 * the sale detail contains a Supabase UUID (or vice versa)
 */
const findUserByAnyId = (usersDict, ...ids) => {
  // Try direct lookup with each provided ID
  for (const id of ids) {
    if (id && usersDict[id]) {
      return usersDict[id]
    }
  }

  // If direct lookup fails, try to find by firestoreId or id match in values
  const userIds = ids.filter(id => id)
  if (userIds.length > 0) {
    for (const user of Object.values(usersDict)) {
      // Check if any of the provided IDs match the user's id or firestoreId
      if (userIds.includes(user.id) || userIds.includes(user.firestoreId)) {
        return user
      }
    }
  }

  return null
}

/**
 * Find a store by any ID format (Supabase UUID or Firestore ID)
 * This handles the case where storesDict might be keyed by Firestore ID but
 * the sale detail contains a Supabase UUID (or vice versa)
 */
const findStoreByAnyId = (storesDict, ...ids) => {
  // Try direct lookup with each provided ID
  for (const id of ids) {
    if (id && storesDict[id]) {
      return storesDict[id]
    }
  }

  // If direct lookup fails, try to find by firestoreId or id match in values
  const storeIds = ids.filter(id => id)
  if (storeIds.length > 0) {
    for (const store of Object.values(storesDict)) {
      // Check if any of the provided IDs match the store's id or firestoreId
      if (storeIds.includes(store.id) || storeIds.includes(store.firestoreId)) {
        return store
      }
    }
  }

  return null
}

export default function SaleDetailScreen( { navigation, route } ) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const toast = useToast()
  const [context, setContext] = useContext(UserContext)
  const usersDict = useAtomValue(usersDictAtom)
  const organization = useAtomValue(organizationAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const userLocale = useAtomValue(userLocaleAtom)
  const priceTypes = useAtomValue(priceTypesAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const { detail, itemIndex, sectionIndex, editItemInData } = route.params
  const [whatsappModalVisible, setWhatsappModalVisible] = useState(false)
  const [phoneValue, setPhoneValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [fiscalSuccess, setFiscalSuccess] = useState(false)
  const [paymentDate, setPaymentDate] = useState(detail.paymentDate || new Date())
  const [paymentMethod, setPaymentMethod] = useState(detail.paymentMethod)
  const [isPaid, setIsPaid] = useState(detail.paymentMethod !== null ? true : false)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [printMenuVisible, setPrintMenuVisible] = useState(false)
  const currency = useAtomValue(currencyAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const _datePicker = React.createRef()

  /**
   * ATL-1274 — drop this sale from New Sale's till-local unfiscalized register once it is signed.
   * Called only after the OFD has actually returned a `fiscalInfo`: the register stands for "New
   * Sale could not sign this", and clearing it on anything less would retire the banner while the
   * receipt is still missing.
   */
  const forgetSaleInUnfiscalizedRegister = () => {
    const saleId = unfiscalizedSaleKey(detail)
    if (!saleId) return
    setContext(ctx => ({...ctx, unfiscalizedSales: forgetResolvedSales(ctx.unfiscalizedSales, [saleId])}))
  }
  
  // Get sale print templates from atom (real-time listening set up elsewhere)
  const availableTemplates = useAtomValue(salePrintTemplatesAtom)

  // Debug logging for dual-write e2e tests
  React.useEffect(() => {
    if (detail) {
      console.log('[SALE_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        organizationId: organization?.id,
        finalTotalCartPrice: detail.finalTotalCartPrice || detail.totalCartPrice || detail.totalPrice,
        totalCartPrice: detail.totalCartPrice,
        discountPercentage: detail.discountPercentage ?? detail.discount,
        discountAmount: detail.discountAmount,
        discountPrice: detail.discountPrice,
        paymentMethod: detail.paymentMethod?.id || detail.paymentMethod,
        secondPaymentMethod: detail.secondPaymentMethod?.id,
        mixedPayment: detail.mixedPayment,
        purchaseMethod: detail.purchaseMethod?.id || detail.purchaseMethod,
        delivery: detail.delivery,
        deliveryPrice: detail.deliveryPrice,
        deliveryAddress: detail.deliveryAddress,
        deliveryPaidByCustomer: detail.deliveryPaidByCustomer,
        client: detail.client ? {
          id: detail.client.id,
          displayName: detail.client.displayName,
          phoneNumber: detail.client.phoneNumber,
        } : null,
        bonusAction: detail.bonusAction,
        bonusReduction: detail.bonusReduction,
        bonusAccumulation: detail.bonusAccumulation,
        punchCardReduction: detail.punchCardReduction,
        postPaid: detail.postPaid,
        cartItemCount: (detail.cartItems || []).length,
        cartItemNames: (detail.cartItems || []).map(i => i.name),
        tableNumber: detail.tableNumber,
        comment: detail.comment,
        priceType: detail.priceType,
        store: detail.store || detail.storeId,
      }), null, 2));

      // Log return data if sale has a return
      if (detail.returnInfo && detail.returnId) {
        console.log('[RETURN_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
          id: detail.returnId,
          saleId: detail.id,
          organizationId: organization?.id,
          totalReturnedAmount: detail.returnInfo.totalReturnedAmount,
          returnType: detail.returnInfo.returnType,
          returnMethod: detail.returnInfo.returnMethod?.id || detail.returnInfo.returnMethod,
          updateStock: detail.returnInfo.updateStock,
          returnReason: detail.returnInfo.returnReason?.text || detail.returnInfo.returnReason,
          returnComment: detail.returnInfo.returnComment,
          store: detail.returnInfo.store || detail.store || detail.storeId,
        })));
      }
    }
  }, [detail, organization]);

  // Print internal receipt
  const handlePrintInternalReceipt = async () => {
    try {
      const detailStore = detail.store || detail.storeId
      const cashierUser = findUserByAnyId(usersDict, detail.cashierUID, detail.cashierId)
      const detailSaleDate = detail.saleDate?.toDate ? detail.saleDate.toDate() : (detail.saleDate instanceof Date ? detail.saleDate : new Date(detail.saleDate || new Date()))
      let receipt = generateInternalReceipt(detail, organization?.name, storesDict?.[detailStore]?.name, (cashierUser?.name || context.user.name), getLocalStoreDate(detailStore, detailSaleDate), currencyFormatter, productUnitsDict, t('pcs'), storesDict?.[detailStore]?.address)          
      const saleTitle = t('invoice.sale') + ' - ' + format(getLocalStoreDate(detailStore, detailSaleDate), 'd MMMM y HH:mm')
      navigation.setOptions({
        title: saleTitle
      })
      if (Platform.OS === 'web') {
        await printInIframe(
          saleTitle,
          receipt
        )
      } else {
        const html = '<html><head><title>' + saleTitle + '</title>' + '<style> div {white-space:pre-wrap; margin: 10px 0;} </style>' + '</head><body >'
        + receipt + '</body></html>'
        await safePrintAsync({ html }, { toast, t })
      }
    } catch (error) {
      console.error('Error printing internal receipt:', error)
    }
  }
  
  // Print invoice with specific template
  const handlePrintInvoice = async (template = null) => {
    try {
      // Get the appropriate print template for this sale
      const detailStore = detail.store || detail.storeId
      const cashierUser = findUserByAnyId(usersDict, detail.cashierUID, detail.cashierId)
      const detailSaleDate = detail.saleDate?.toDate ? detail.saleDate.toDate() : (detail.saleDate instanceof Date ? detail.saleDate : new Date(detail.saleDate || new Date()))
      const templateSettings = template ? template.settings : getPrintTemplateSettings('sale', detailStore)

      // Preload product images if product images are enabled in the template
      if (templateSettings?.includeFields?.productImages && productsDict) {
        await preloadProductImages(detail, productsDict)
      }

      // Preload logo image if enabled in the template
      await preloadLogoImage(templateSettings)

      // Preload extra image if enabled in the template
      await preloadExtraImage(templateSettings)

      const localStoreDate = getLocalStoreDate(detailStore, detailSaleDate)
      const invoiceNumber = generateInvoiceNumber(localStoreDate)
      let receipt = generateExpenseInvoice(detail, organization?.name, storesDict?.[detailStore]?.name, (cashierUser?.name || context.user.name), localStoreDate, currencyFormatter, currency, productUnitsDict, t('pcs'), storesDict?.[detailStore]?.address, invoiceNumber, null, userLocale, templateSettings, t, productsDict)          
      const saleTitle = t('invoice.sale') + ' - ' + format(getLocalStoreDate(detailStore, detailSaleDate), 'd MMMM y HH:mm')
      navigation.setOptions({
        title: saleTitle
      })
      if (Platform.OS === 'web') {
        await printInIframe(
          saleTitle,
          receipt
        )
      } else {
        const html = '<html><head><title>' + saleTitle + '</title>' + '<style> div {white-space:pre-wrap; margin: 10px 0;} </style>' + '</head><body >'
        + receipt + '</body></html>'
        await safePrintAsync({ html }, { toast, t })
      }
    } catch (error) {
      console.error('Error printing invoice:', error)
      Sentry.captureException(error)
      // Fallback to old method if template loading fails
      const detailStore = detail.store || detail.storeId
      const cashierUserFallback = findUserByAnyId(usersDict, detail.cashierUID, detail.cashierId)
      const detailSaleDate = detail.saleDate?.toDate ? detail.saleDate.toDate() : (detail.saleDate instanceof Date ? detail.saleDate : new Date(detail.saleDate || new Date()))
      const localStoreDate = getLocalStoreDate(detailStore, detailSaleDate)
      const invoiceNumber = generateInvoiceNumber(localStoreDate)
      let receipt = generateExpenseInvoice(detail, organization?.name, storesDict?.[detailStore]?.name, (cashierUserFallback?.name || context.user.name), localStoreDate, currencyFormatter, currency, productUnitsDict, t('pcs'), storesDict?.[detailStore]?.address, invoiceNumber, null, userLocale, constants?.printSettings?.invoiceSettings, t, productsDict)          
      const saleTitle = t('invoice.sale') + ' - ' + format(getLocalStoreDate(detailStore, detailSaleDate), 'd MMMM y HH:mm')
      navigation.setOptions({
        title: saleTitle
      })
      if (Platform.OS === 'web') {
        const fallbackHtml = '<html><head><title>' + saleTitle + '</title>' + '<style> div {white-space:pre-wrap; margin: 10px 0;} </style>' + '</head><body >'
         + receipt + '</body></html>'
        const mywindow = window.open('', 'PRINT', 'height=400,width=600')
        if (mywindow) {
          mywindow.document.write(fallbackHtml)
          mywindow.document.close()
          mywindow.focus()
          mywindow.onafterprint = function() {mywindow.close()}
          mywindow.print()
        }
      } else {
        const fallbackHtml = '<html><head><title>' + saleTitle + '</title>' + '<style> div {white-space:pre-wrap; margin: 10px 0;} </style>' + '</head><body >'
        + receipt + '</body></html>'
        await safePrintAsync({ html: fallbackHtml }, { toast, t })
      }
    }
  }
  
  return (
    <SafeAreaView style={{flex: 1}}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        <Text appearance='hint' style={{textAlign: 'center', marginVertical: 10}}>
          {detail.id}
        </Text>
        <View style={styles.detailRow}>
          <View style={styles.detailInfo}>
            {detail.paymentMethod && (detail.paymentMethod?.id || typeof detail.paymentMethod === 'string') && ( // old sales payment methods did not have id
              <FontAwesome5 name={detail.paymentMethod?.icon || detail.paymentMethod} size={16} color={theme['text-basic-color']} style={styles.icon}/>
            )}
            {!detail.paymentMethod && (
              <FontAwesome5 name='ban' size={16} color={colors.red} style={styles.icon}/>
            )}
            {detail.secondPaymentMethod && (
              <FontAwesome5 name={detail.secondPaymentMethod?.icon || detail.secondPaymentMethod} size={16} color={theme['text-basic-color']} style={styles.icon}/>
            )}
            <Text
              style={styles.itemTitle}
              category='s2'>
              {currencyFormatter.format(detail.finalTotalCartPrice || detail.totalCartPrice || detail.totalPrice || 0)}
            </Text>
          </View>
          <View style={styles.detailInfo}>
            <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
            <Text
              style={styles.itemTitle}
              category='s2'>
              {(findUserByAnyId(usersDict, detail.cashierUID, detail.cashierId)?.name || 'Unknown') + ', ' + (findStoreByAnyId(storesDict, detail.store, detail.storeId)?.name || '')}
            </Text>
          </View>
          <View style={styles.detailInfo}>
            { detail.purchaseMethod?.id ? (
              <FontAwesome5 name={detail.purchaseMethod?.icon || detail.purchaseMethod} size={14} color={theme['text-basic-color']} style={styles.icon}/>
            ) : ( // to support old
              <>
                {(detail.purchaseMethod == 0 || detail.purchaseMethod === '0') && (<FontAwesome5 name="building" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
                {(detail.purchaseMethod == 1 || detail.purchaseMethod === '1') && (<FontAwesome5 name="walking" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
                {(detail.purchaseMethod == 2 || detail.purchaseMethod === '2') && (<FontAwesome5 name="taxi" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
              </>
            ) }
            <Text
              style={styles.itemTitle}
              category='s2'>
              {format(getLocalStoreDate(detail.store || detail.storeId, detail.saleDate?.toDate ? detail.saleDate.toDate() : (detail.saleDate instanceof Date ? detail.saleDate : new Date(detail.saleDate || new Date()))), "d MMMM y HH:mm")}
            </Text>
          </View>
          {detail.client != null && (
            <View style={styles.detailInfo}>
              <TouchableOpacity onPress={() => {
                  if (['admin'].includes(context.user.role)) {
                    navigation.navigate('ClientDetail', {client: detail.client, requiresUpdate: true})
                  }
                }}
                style={{flexDirection: 'row', alignItems: 'center'}}
              >
                <Icon name='heart' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
                <View style={styles.itemTitle}>
                  <Text category='s2'>{detail.client?.phoneNumber || ''}</Text>
                  <Text category='s2'>{detail.client?.displayName || ''}</Text>
                </View>
              </TouchableOpacity>
            </View>
          )}
          {(detail.tableNumber != null && detail.tableNumber !== '') && (
            <View style={styles.detailInfo}>
              <Icon name='hash-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
              <Text
                style={styles.itemTitle}
                category='s2'>
                {detail.tableNumber}
              </Text>
            </View>
          )}
          {(detail.comment != null && detail.comment !== '') && (
            <View style={styles.detailInfo}>
              <Icon name='message-square-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
              <Text
                style={styles.itemTitle}
                category='s2'>
                {detail.comment}
              </Text>
            </View>
          )}
          {detail.priceType && (() => {
            // detail.priceType is a string id in Firestore mode, but an object
            // ({ id, firestoreId, name, text }) in Supabase mode. Resolve to a
            // displayable string — never render the raw object as a React child.
            const ptId = typeof detail.priceType === 'object' && detail.priceType !== null
              ? (detail.priceType.id ?? detail.priceType.firestoreId)
              : detail.priceType
            const ptName = (typeof detail.priceType === 'object' && detail.priceType !== null
              ? (detail.priceType.name ?? detail.priceType.text)
              : null)
              || priceTypes.find(pt => pt.id === ptId)?.name
              || (typeof ptId === 'string' ? ptId : '')
            return (
              <View style={styles.detailInfo}>
                <Icon name='pricetags-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
                <Text style={styles.itemTitle} category='s2'>
                  {(t('salesHistory.priceType') || 'Price Type') + ': ' + ptName}
                </Text>
              </View>
            )
          })()}
        </View>

        {detail.delivery != null && (
          <Card status='basic' style={{marginTop: 15}}>
            <View style={{flexDirection: 'row', alignItems: 'center'}}>
              <Icon name='car-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
              <Text>
                {t('salesHistory.delivery')}
              </Text>
            </View>
            <Text
              style={styles.returnInfoText}
            >
              {t('salesHistory.deliveryAddress') + ' - ' + detail.deliveryAddress}
            </Text>
            <Text
              style={styles.returnInfoText}
            >
              {t('salesHistory.deliveryPrice') + ' - ' + currencyFormatter.format(detail.deliveryPrice)}
            </Text>
            <Text
              style={styles.returnInfoText}
            >
              {(detail.deliveryPaidByCustomer ? t('salesHistory.deliveryPaidByCustomer') : t('salesHistory.deliveryPaidBySeller'))}
            </Text>
            { detail.deliverySpendingId && (
              <TouchableOpacity style={styles.returnInfoText} onPress={() => navigation.navigate('SpendingDetail', {detailId: detail.deliverySpendingId})}>
                <View style={{flexDirection: 'row', alignItems: 'flex-end'}}>
                  <Text style={{color: colors.primary}}>{t('spendings.spending')}</Text>
                  <Icon name='external-link-outline' width={16} height={16} fill={colors.primary} style={{marginLeft: 4}} /> 
                </View>
              </TouchableOpacity>
            )}
          </Card>
        )}

        {detail.returnInfo != null && (
          <Card status='danger' style={{marginTop: 15}}>
            <View style={{flexDirection: 'row', alignItems: 'center'}}>
              <Icon name='swap-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
              <Text>
                {t('salesHistory.returnFiled')}
              </Text>
            </View>
            <Text
              style={styles.returnInfoText}
            >
              {t('salesHistory.date') + format(getLocalStoreDate(detail.store || detail.storeId, detail.returnInfo?.date?.toDate ? detail.returnInfo.date.toDate() : (detail.returnInfo?.date instanceof Date ? detail.returnInfo.date : new Date(detail.returnInfo?.date || new Date()))), "d MMMM y HH:mm")}
            </Text>
            <Text
              style={styles.returnInfoText}
            >
              {t('salesHistory.reason') + detail.returnInfo.returnReason.text}
            </Text>
            {detail.returnInfo.returnComment != '' && (<Text
              style={styles.returnInfoText}
              category='s2'>
              {t('salesHistory.comment') + ' - ' + detail.returnInfo.returnComment}
            </Text>)}
            <Text
              style={styles.returnInfoText}
            >
              {t('salesHistory.returnAmount') + detail.returnInfo.totalReturnedAmount}
            </Text>
            <Text
              style={styles.returnInfoText}
            >
              {t('salesHistory.returnType') + ' - ' + (detail.returnInfo.returnType === 0 ? t('salesHistory.replaceProduct') : t('salesHistory.returnMoney'))}
            </Text>
            {detail.returnInfo.updateStock != null && (
              <Text
                style={styles.returnInfoText}
              >
                {t('salesHistory.returnUpdateStockText') + ' - ' + (detail.returnInfo.updateStock ? t('salesHistory.takeBack') : t('salesHistory.writeOff'))}
              </Text>
            )}
          </Card>
        )}
        
        <CartView 
          style={{marginTop: 15}}
          sale={detail}
          cartItems={detail.cartItems || []} 
          discountPercentage={detail.discountPercentage ?? detail.discount}  // detail.discount - for old sales before Dec 10 2024
          discountAmount={detail.discountAmount}
          purchaseMethod={detail.purchaseMethod}
          store={detail.store || detail.storeId}
          client={detail.client}
          bonusAction={detail.bonusAction}
          cashbackEnabled={detail.cashbackEnabled}
        />

        { detail.fiscalInfo ? (
          <>
            <Modal style={styles.modal} visible={whatsappModalVisible} backdropStyle={styles.backdrop} onBackdropPress={() => setWhatsappModalVisible(false)}>
              <Card style={styles.modalContainer}>
                <Text>{t('salesHistory.inputPhoneNumber')}</Text>
                <Input
                  placeholder='77788047045'
                  value={phoneValue}
                  keyboardType='phone-pad'
                  autoFocus={true}
                  onChangeText={setPhoneValue}
                  style={styles.spacedItem}
                />
                <WhatsAppButton
                  testID='sale-receipt-whatsapp'
                  status='primary'
                  style={styles.spacedItem}
                  phone={phoneValue}
                  allowChatPicker
                  text={() => fiscalReceiptShareText(detail.fiscalInfo, base64.decode) ?? ''}
                >
                  {t('salesHistory.send')}
                </WhatsAppButton>
                <Button status='basic' style={styles.spacedItem} onPress={() => setWhatsappModalVisible(false)}>
                  {t('cancel')}
                </Button>
              </Card>
            </Modal>

            <Button status='success' style={styles.button} onPress={() => {
              if (!fiscalReceiptShareText(detail.fiscalInfo, base64.decode)) {
                alert(t('salesHistory.receiptContentUnavailable'))
                return
              }
              setWhatsappModalVisible(true)
            }} testID="send-to-whatsapp-button">
              {t('salesHistory.sendToWhatsapp')}
            </Button>
            
           
            <Button style={styles.button} testID="print-receipt-button" onPress={async () => {
              const receiptBody = fiscalReceiptPrintBody(detail.fiscalInfo, base64.decode)
              if (!receiptBody) {
                alert(t('salesHistory.receiptContentUnavailable'))
                return
              }
              if (Platform.OS === 'web') {
                var html = '<html><head><title>Receipt</title>' + '<style> div {white-space:pre-wrap;} </style>' + '</head><body >'
                + '<div>' + receiptBody + '</div>' + '</body></html>'
                var mywindow = window.open('', 'PRINT', 'height=400,width=600')
                if (mywindow) {
                  mywindow.document.write(html)
                  mywindow.document.close()
                  mywindow.focus()
                  mywindow.onafterprint = function() {mywindow.close()}
                  mywindow.print()
                }
              } else {
                var html = '<html><head><title>Receipt</title>' + '<style> div {white-space:pre-wrap;} </style>' + '</head><body >'
                + '<div>' + receiptBody + '</div>' + '</body></html>'
                await safePrintAsync({ html }, { toast, t })
              }
            }}>
              {t('salesHistory.printReceipt')}
            </Button>
            

            { Platform.OS !== 'web' && (
              <Button style={styles.button} appearance='outline' onPress={async() => {
                const receiptText = fiscalReceiptShareText(detail.fiscalInfo, base64.decode)
                if (!receiptText) {
                  alert(t('salesHistory.receiptContentUnavailable'))
                  return
                }
                const isAvailable = await SMS.isAvailableAsync();
                if (isAvailable) {
                  await SMS.sendSMSAsync([], receiptText)
                } else {
                  alert(t('salesHistory.noSms'))
                }
              }}>
                {t('salesHistory.sendToSms')}
              </Button>
            )}

            <Button style={styles.button} appearance='outline' status='basic' testID="copy-receipt-button" onPress={() => {
              const receiptText = fiscalReceiptShareText(detail.fiscalInfo, base64.decode)
              if (!receiptText) {
                alert(t('salesHistory.receiptContentUnavailable'))
                return
              }
              if (Platform.OS === 'web') {
                const el = document.createElement('textarea')
                el.value = receiptText
                document.body.appendChild(el)
                el.select()
                document.execCommand('copy')
                document.body.removeChild(el)
              } else {
                Clipboard.setString(receiptText)
              }
            }}>
              {t('salesHistory.copyReceipt')}
            </Button>

          </>
        ) : (
          isFiscalizationEnabled(storesDict?.[detail.store || detail.storeId]) && (detail.paymentMethod) && 
            <>
              {fiscalSuccess ? (
                <Text status='success' style={styles.label}>
                  {t('success')}
                </Text>
              ) : (
                <Text status='danger' style={styles.label}>
                  {t('salesHistory.notFiscalized')}
                </Text>
              )}
              
              {isLoading ? (
                <View style={styles.spinner}><Spinner /></View>
              ) : (
                <Button style={styles.button} status='primary' testID="retry-ofd-button" onPress={async() => {
                  setIsLoading(true)
                  // ATL-1170 review: `setIsLoading(false)` used to sit after the branch rather than in a
                  // `finally`. Anything that threw in here — `updateSale`, `saveFiscalOperation`, an OFD
                  // call that rejects rather than resolving falsy — left the retry button replaced by a
                  // spinner that never cleared, with no way back except leaving the screen.
                  try {
                    // A SECOND receipt for a sale is a tax event nothing can take back, and `detail`
                    // may be the Supabase mirror, which lags the Firestore write master (a till
                    // that signed this sale can still be holding the mirror in its outbox). Ask the
                    // master first; only an explicit "no receipt there" may sign (utils/fiscalRetryGuard).
                    const master = await readMasterFiscalState(detail)
                    if (master.kind === 'fiscalized') {
                      // The row this screen read lacked the receipt, so the mirror of the original
                      // signing never landed: re-send it (Supabase only — Firestore already has it).
                      try {
                        await mirrorSaleFiscalInfo(detail, master.fiscalInfo)
                      } catch (error) {
                        Sentry.captureException(error, { tags: { books_write_error: 'sale-fiscal-info-mirror' } })
                      }
                      editItemInData?.({...detail, fiscalInfo: master.fiscalInfo}, sectionIndex, itemIndex)
                      setFiscalSuccess(true)
                      forgetSaleInUnfiscalizedRegister()
                      alert(t('salesHistory.alreadyFiscalized'))
                      return
                    }
                    if (master.kind === 'unknown') {
                      alert(t('salesHistory.fiscalStateUnverifiable'))
                      return
                    }
                    const detailStore = detail.store || detail.storeId
                    if (storesDict?.[detailStore]?.fiscalOperator?.id === 'light-kassa') {
                      let fiscalInfo = await saveFiscalOperation(storesDict?.[detailStore].LKCredentials, detail, detailStore)
                      if (!fiscalInfo) {
                        await setNewLKTokens(storesDict?.[detailStore].LKCredentials, detailStore)
                        fiscalInfo = await saveFiscalOperation(storesDict?.[detailStore].LKCredentials, detail, detailStore)
                      }
                      if (!fiscalInfo) {
                        alert(t('salesHistory.ofdError'))
                      } else {
                        await updateSale(detail, {fiscalInfo})
                        editItemInData?.({...detail, fiscalInfo}, sectionIndex, itemIndex)
                        setFiscalSuccess(true)
                        forgetSaleInUnfiscalizedRegister()
                      }
                    } else if (storesDict?.[detailStore]?.fiscalOperator?.id === 'wipon-kassa') {
                      // ATL-1170: this was the same re-login-and-retry the sale path lacked, written
                      // out a third time. It now shares the one tested implementation, which also
                      // persists the refreshed credentials instead of leaving that write floating.
                      const {fiscalInfo, reason, message, responseClass, rejection} = await fiscalizeSale(
                        storesDict?.[detailStore]?.wiponCredentials,
                        detail,
                        (refreshed) => updateStoreWiponCredentials(storesDict?.[detailStore]?.ref, refreshed),
                      )
                      if (fiscalInfo) {
                        await updateSale(detail, {fiscalInfo})
                        editItemInData?.({...detail, fiscalInfo}, sectionIndex, itemIndex)
                        setFiscalSuccess(true)
                        forgetSaleInUnfiscalizedRegister()
                      } else {
                        // ATL-1274 — `fiscalizeSale` used to raise this dialog itself, from inside
                        // the provider module, on every one of its give-up paths. That is what made
                        // it unremovable from the SALE path, where it interrupted checkout once per
                        // sale. It reports now, and the surface decides: this is a button the user
                        // pressed and stayed to watch, so a dialog with the provider's own words is
                        // the right answer here and a non-blocking banner is the right answer there.
                        // Still exactly ONE dialog — the reason and the provider message arrive
                        // together, so there is nothing left to stack a generic «ошибка ОФД» onto.
                        const detailText = fiscalFailureDetail({reason, message}, t)
                        alert(detailText ? t('salesHistory.ofdErrorWithReason', {reason: detailText}) : t('salesHistory.ofdError'))
                        // A retry that fails is how we learn the provider is still refusing, and
                        // before ATL-1170 nothing here reached Sentry.
                        Sentry.captureException(new Error(`Wipon fiscalization retry failed: ${reason}`), {
                          tags: {fiscalOperator: 'wipon-kassa', fiscalFailureReason: reason, fiscalResponseClass: responseClass || 'none', fiscalResponseCode: rejection?.code || 'none'},
                          extra: {store: detailStore, saleRef: detail?.ref?.id || detail?.id, wiponRejection: rejection},
                        })
                      }
                    }
                  } catch (error) {
                    Sentry.captureException(error, { tags: { books_write_error: 'sale-fiscal-retry' } })
                    alert(t('salesHistory.ofdError'))
                  } finally {
                    setIsLoading(false)
                  }
                }}>
                  {t('salesHistory.retryOfd')}
                </Button>
              )}
            </>
          )
        }
        
        { !detail.fiscalInfo && (
          <OverflowMenu
            visible={printMenuVisible}
            onBackdropPress={() => setPrintMenuVisible(false)}
            onSelect={() => setPrintMenuVisible(false)}
            anchor={() => (
              <Button
                style={styles.button}
                onPress={() => setPrintMenuVisible(true)}
                accessoryRight={(props) => <Icon {...props} name='arrow-down' />}
                testID="print-options-button"
              >
                {t('salesHistory.printOptions')}
              </Button>
            )}
          >
            <MenuItem 
              title={t('salesHistory.printInternalReceiptOption')}
              onPress={() => {
                setPrintMenuVisible(false)
                handlePrintInternalReceipt()
              }}
            />
            {availableTemplates.map((template, index) => (
              <MenuItem 
                key={template.id || index}
                title={template.name || `${t('salesHistory.printTemplatesOption')} ${index + 1}`}
                onPress={() => {
                  setPrintMenuVisible(false)
                  handlePrintInvoice(template)
                }}
              />
            ))}
            <MenuItem 
              title={t('settings.printSettings')}
              onPress={() => {
                setPrintMenuVisible(false)
                navigation.navigate('PrintSettings')
              }}
              accessoryRight={(props) => <Icon {...props} name='settings-2-outline' />}
            />
          </OverflowMenu>
        )}  

        { detail.returnFiscalInfo && (
          <Button style={styles.button} onPress={async () => {
            const returnReceiptBody = fiscalReceiptPrintBody(detail.returnFiscalInfo, base64.decode)
            if (!returnReceiptBody) {
              alert(t('salesHistory.receiptContentUnavailable'))
              return
            }
            const detailStore = detail.store || detail.storeId
            const detailSaleDate = detail.saleDate?.toDate ? detail.saleDate.toDate() : (detail.saleDate instanceof Date ? detail.saleDate : new Date(detail.saleDate || new Date()))
            const saleTitle = t('invoice.sale') + ' - ' + format(getLocalStoreDate(detailStore, detailSaleDate), 'd MMMM y HH:mm')
            navigation.setOptions({
              title: saleTitle
            })
            if (Platform.OS === 'web') {
              await printInIframe(
                saleTitle,
                '<div>' + returnReceiptBody + '</div>'
              )
            } else {
              var html = '<html><head><title>' + saleTitle + '</title>' + '<style> div {white-space:pre-wrap; margin: 10px 0;} </style>' + '</head><body >'
              + '<div>' + returnReceiptBody + '</div>' + '</body></html>'
              await safePrintAsync({ html }, { toast, t })
            }
          }}>
            {t('salesHistory.printReturnReceipt')}
          </Button>
        )}

        { !detail.paymentMethod && (
          <View style={{marginTop: 15}}>
            <Text category='label' style={{marginBottom: 5}}>{t('salesHistory.salePayment')}</Text>
            <Toggle 
              checked={isPaid}
              onChange={(isChecked) => {
                setIsPaid(isChecked)
              }}
              style={{marginBottom: 10, alignSelf: 'flex-start'}}
            >
              {isPaid ? t('salesHistory.paid') : t('salesHistory.notPaid')}
            </Toggle>
            { isPaid && (
              <View style={{backgroundColor: 'white', padding: 20, borderRadius: 5}}>
                <Text category='label' style={{marginBottom: 5}}>
                  {t('salesHistory.paymentDate')}
                </Text>
                <Datepicker
                  placement='top'
                  date={paymentDate}
                  dateService={localeDateService}
                  onSelect={(date) => {
                    let currentDate = new Date()
                    date.setHours(currentDate.getHours(), currentDate.getMinutes())
                    setPaymentDate(date)
                    _datePicker.current.blur()
                  }}
                  min={new Date('2020-12-01')}
                  ref={_datePicker}
                  style={{marginBottom: 10}}
                />
                <PaymentMethodsSelect paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} paymentMethods={constants?.paymentMethods.data} style={{marginBottom: 15}} store={detail.store || detail.storeId} />
                {saveError != '' && (
                  <Text category='p1' status='danger' style={styles.label}>
                    {saveError}
                  </Text>
                )}
                { successMessageVisible && (
                  <>
                  <Text status='success'>{t('success')}</Text>
                  <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
                    {t('back')}
                  </Button>
                  </>
                )}
                { isLoading ? (
                  <View style={styles.spinner}><Spinner /></View>
                ) : (
                  !successMessageVisible && 
                  <Button status='primary' onPress={async() => {
                    if (paymentMethod) {
                      setIsLoading(true)
                      try {
                        const detailStore = detail.store || detail.storeId
                        await markSalePaid(context.db, detail, {paymentMethod, paymentDate, dailyStatID: getDailyStatID(detailStore, paymentDate)}, 
                          getFiscalOperator(storesDict?.[detailStore]),
                          getFiscalCredentials(storesDict?.[detailStore])
                        )
                        setSuccessMessageVisible(true)
                        setPaymentMethod(null)
                      } catch(error) {
                        console.log(error)
                        setSaveError(t('error'))
                        Sentry.captureException(error, { tags: { books_write_error: 'sale-mark-paid' } })
                      }
                      setIsLoading(false)
                    }
                  }}>
                    {t('save')}
                  </Button>
                )}
              </View>
            )}
          </View>
        )}
{/* 
        {editMode && <Divider color='black' width='100%' />}

        {editMode
          ?
            <Button 
              status='success' 
              appearance='outline'  
              style={styles.button}
              onPress={async () => {
                var updatedInfo = {purchaseMethod, paymentMethod}
                if (purchaseMethod == 2) {
                  updatedInfo['deliveryAmount'] = parseInt(deliveryAmount) || 0
                  updatedInfo['deliveryPaidByUs'] = deliveryPaidByUs
                } else {
                  // TODO: this below doesn't delete fields in Firestore document if they exist
                  updatedInfo['deliveryAmount'] = deleteField
                  updatedInfo['deliveryPaidByUs'] = deleteField
                }
                try {
                  await updateSaleWithStats(db, sale, updatedInfo)
                } catch(error) {
                  Sentry.captureException(error)
                }
                
                setEditMode(false)
                navigation.goBack()
              }}
            >  
              {t('save')}
            </Button>
          :  
            <Button 
              status='warning' 
              appearance='outline'  
              style={styles.button}
              onPress={() => {
                setEditMode(true)
              }}
            >  
              {t('edit')}
            </Button>
        }

        {editMode && sale.paymentMethod?.id && (
          <>
            <PurchaseMethodsSelect purchaseMethod={purchaseMethod} setPurchaseMethod={setPurchaseMethod} purchaseMethods={constants?.purchaseMethods.data} />
            <PaymentMethodsSelect paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} paymentMethods={constants?.paymentMethods.data} />
            <Divider color='black' width='100%' />
          </>
        )} */}

        { detail.returnInfo == null ? (
          <Button
            onPress={() => {navigation.navigate('ReturnSale', {sale: detail})}}
            status='danger'
            appearance='outline'
            style={styles.lastButton}
            testID="file-return-button"
          >
            {t('salesHistory.fileReturn')}
          </Button>
        ) : ((userRole?.permissions?.deleteSale || context.user.role === 'admin') && detail.returnId &&
          <>
           {saveError != '' && (
              <Text category='p1' status='danger' style={styles.label}>
                {saveError}
              </Text>
            )}
            { isLoading ? (
              <View style={styles.spinner}><Spinner /></View>
            ) : (
              !successMessageVisible && 
                <Button
                  onPress={async () => {
                    // get return info and delete it
                    try {
                      setIsLoading(true)
                      let querySnap = await context.db.collection('returns').doc(detail.returnId).get()
                      await deleteSaleReturn(context.db, querySnap.ref, detail.ref, detail, querySnap.data())
                      navigation.navigate('Home')
                      setIsLoading(false)
                    } catch(error) {
                      Sentry.captureException(error, { tags: { books_write_error: 'sale-return-delete' } })
                      console.log(error)
                      setSaveError(t('error'))
                    }
                  }}
                  status='danger'
                  appearance='outline'
                  style={styles.lastButton}
                  testID="delete-return-button"
                >
                  {t('salesHistory.deleteReturn')}
                </Button>
            )}
          </>
        )}
       <View style={{height: 200}}></View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  }, 
  icon: {
    marginRight: 4,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    flexWrap: 'wrap',
  },
  detailInfo: {
    flex: 1,
    flexDirection: 'row', 
    justifyContent: 'flex-start',
    alignItems: 'center',
    minWidth: 100,
    marginRight: 10,
    marginBottom: 10,
  },
  itemTitle: {
    flex: 4,
    paddingLeft: 4,
    // textAlign: 'center',
  },
  returnInfoText: {
    // marginLeft: 20
  },
  button: {
    marginTop: 15,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 300,
    height: '80%',
    padding: 16,
    margin: 32
  },
  spacedItem: {
    marginVertical: 4,
  },
  modal: {
    top: 100,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  spinner: {
    marginVertical: 10,
    alignItems: 'center'
  },
  lastButton: {
    marginTop: 15,
    marginBottom: 60,
  },
  label: {
    marginTop: 10
  }
});
